/**
 * WEB-672 phase 2a — FE mirror of the spans-JSON rich-text contract.
 * Source of truth: backend/src/ai-funnels/block-schema/rich-text.ts
 * (format ratified by cto-duo 2026-07-17, APPROVED spans).
 *
 *   type RichMark  = 'bold' | 'italic' | 'underline' | 'strike';
 *   type RichSpan  = { text: string; marks?: RichMark[]; color?: string; href?: string };
 *   type RichField = string | RichSpan[];   // old plain strings stay valid
 *
 * color — exactly two valid forms (mirrors the BE write gate; the FE
 * re-validates literals as defense-in-depth on the render path):
 *   - `token:<paletteKey>` — resolved against ThemeColors / theme CSS vars.
 *     NB: the BE key `buttonTextColor` maps to the FE ThemeColors key
 *     `onPrimary` (see paletteToStyle: buttonTextColor → --color-on-primary).
 *   - a literal `#hex` (3/4/6/8 digits) or `rgb()/rgba()` with numeric
 *     components. Anything else (url(), var(), keywords, multi-declaration
 *     smuggling) is ignored at render time — the span renders WITHOUT color.
 */
import type { ThemeColors } from '@/themes/theme.types';

export type RichMark = 'bold' | 'italic' | 'underline' | 'strike';

export interface RichSpan {
  text: string;
  marks?: RichMark[];
  color?: string;
  href?: string;
}

export type RichText = RichSpan[];

/** Union: old plain string stays valid — no data migration (BE identity migrator). */
export type RichField = string | RichSpan[];

/** Uniform view: plain string becomes a single unformatted span (mirrors BE). */
export function normalizeRichField(value: RichField): RichSpan[] {
  return typeof value === 'string' ? [{ text: value }] : value;
}

/** Plain-text projection: identity for strings, concatenation for spans. */
export function richFieldToPlainText(value: RichField): string {
  return typeof value === 'string' ? value : value.map((span) => span.text).join('');
}

/**
 * Render-gate helper, replaces `block.content && ...` truthiness checks in the
 * renderers. Exact parity with the legacy behavior for plain strings
 * ('' / undefined → false, pre-interpolation), and the natural extension for
 * spans (renders only when the concatenated raw text is non-empty).
 */
export function hasRichText(value: RichField | null | undefined): boolean {
  if (value == null) return false;
  return richFieldToPlainText(value) !== '';
}

/**
 * EditorBlock display fields are typed `string | undefined` (editorSlice is
 * out of scope for phase 2a — widening them is the 2b editor atom). At
 * runtime the BE already persists RichField (string | RichSpan[]) for the
 * ratified fields, so the renderers read them through this cast helper —
 * same defensive pattern as aiFields() in PublicBlockRendererExtended.
 */
export function asRichField(value: string | null | undefined): RichField | undefined {
  return value == null ? undefined : (value as unknown as RichField);
}

// ── Color validation (mirror of BE HEX_COLOR_RE / RGB_COLOR_RE) ─────────────

/** #rgb / #rgba / #rrggbb / #rrggbbaa */
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** rgb(r, g, b) / rgba(r, g, b, a) with strictly numeric components. */
const RGB_COLOR_RE =
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d{1,6}|1\.0{1,6})\s*)?\)$/;

/** True when `value` is a safe literal CSS color (hex or rgb/rgba). */
export function isAllowedLiteralRichColor(value: string): boolean {
  return HEX_COLOR_RE.test(value) || RGB_COLOR_RE.test(value);
}

// ── Token resolution ────────────────────────────────────────────────────────

interface TokenTarget {
  /** Key inside the FE ThemeColors object. */
  themeKey: keyof ThemeColors;
  /** Theme CSS variable the key is emitted as (themeToStyle/paletteToStyle). */
  cssVar: string;
}

/**
 * BE palette key → FE ThemeColors key + theme CSS variable.
 * Mirrors RICH_TEXT_COLOR_TOKENS (BE) and themeToStyle/paletteToStyle (FE):
 * danger → --color-error, background → --color-bg-primary,
 * surface → --color-bg-surface, buttonTextColor → onPrimary/--color-on-primary.
 */
const RICH_COLOR_TOKEN_MAP: Record<string, TokenTarget> = {
  primary: { themeKey: 'primary', cssVar: '--color-primary' },
  primaryHover: { themeKey: 'primaryHover', cssVar: '--color-primary-hover' },
  accent: { themeKey: 'accent', cssVar: '--color-accent' },
  background: { themeKey: 'background', cssVar: '--color-bg-primary' },
  surface: { themeKey: 'surface', cssVar: '--color-bg-surface' },
  textPrimary: { themeKey: 'textPrimary', cssVar: '--color-text-primary' },
  textSecondary: { themeKey: 'textSecondary', cssVar: '--color-text-secondary' },
  border: { themeKey: 'border', cssVar: '--color-border-primary' },
  success: { themeKey: 'success', cssVar: '--color-success' },
  danger: { themeKey: 'danger', cssVar: '--color-error' },
  buttonTextColor: { themeKey: 'onPrimary', cssVar: '--color-on-primary' },
};

const TOKEN_PREFIX = 'token:';

/**
 * WEB-1710 — ПЕРЕМЕННЫЕ-ДВОЙНИКИ для покраски куска текста.
 *
 * Токен образца палитры разворачивался ровно в переменную темы
 * (`var(--color-text-primary)`), а обёртка блока (blockInnerSurface) ту же
 * переменную переопределяет на всё своё поддерево. Итог: автор красил слово
 * вручную, потом менял «Цвет текста внутри элемента» — и слово перекрашивалось
 * вместе со всем блоком, хотя в данных его цвет цел.
 *
 * Развязка: кусок читает СВОЙ двойник `--rich-*`, который объявляет корень
 * воронки (buildFunnelSurfaceStyle) и не трогает ни одна блочная обёртка.
 * Двойник объявлен ССЫЛКОЙ на переменную темы, поэтому подстановка берёт цвет
 * активной темы на корне — смысл токенов (текст едет за темой) сохранён.
 *
 * Запасная ветка `var(--rich-…, var(--color-…))` обязательна: вне корня
 * воронки (окошко палитры в кабинете, галереи тем) двойников нет, и там цвет
 * обязан остаться прежним.
 */
const RICH_COLOR_ALIAS_PREFIX = '--rich-';

function richColorAliasVar(cssVar: string): string {
  return cssVar.replace(/^--color-/, RICH_COLOR_ALIAS_PREFIX);
}

/**
 * Объявление двойников для корня воронки: `{'--rich-text-primary':
 * 'var(--color-text-primary)', …}` по каждому токену палитры. Точка правды —
 * та же таблица RICH_COLOR_TOKEN_MAP, что разворачивает токены при показе,
 * поэтому новый токен получает двойник без второй правки.
 */
export function richColorAliasStyle(): Record<string, string> {
  const style: Record<string, string> = {};
  for (const target of Object.values(RICH_COLOR_TOKEN_MAP)) {
    style[richColorAliasVar(target.cssVar)] = `var(${target.cssVar})`;
  }
  return style;
}

/**
 * WEB-2377 п.2 — ПЕРЕКРЫТИЕ ПЕРЕМЕННОЙ ТЕМЫ ГЛУБЖЕ КОРНЯ ОБЯЗАНО ВЕСТИ ЗА
 * СОБОЙ ДВОЙНИКА.
 *
 * Двойник объявлен ССЫЛКОЙ на переменную темы и ровно ОДИН раз — на корне
 * воронки. Подстановка `var(--color-…)` считается ТАМ ЖЕ, на корне, поэтому
 * любое перекрытие переменной темы на элементе ГЛУБЖЕ (прижатие светлого
 * текста над вуалью и над тёмной заливкой — `ScreenBackgroundWrapper`)
 * двойника не двигает: кусок текста, покрашенный токеном палитры, продолжает
 * читать цвет темы. Тёмный текст оставался тёмным на тёмной вуали, а
 * серверный гард контраста (`enforce-block-contrast.util.ts`) при этом считал
 * экран починенным — он как раз и заменяет тёмный литерал на токен.
 *
 * Функция берёт стиль-перекрытие и дописывает к каждой переменной темы,
 * у которой есть двойник, ТОТ ЖЕ литерал. Набор двойников выводится из
 * таблицы токенов (единственная точка правды, та же, что у
 * `richColorAliasStyle`), а не пишется рядом руками — новый токен палитры
 * получает прижатие без второй правки (шрам WEB-1687).
 */
export function withRichColorAliases<T extends Record<string, string>>(
  overrides: T,
): T & Record<string, string> {
  const aliased: Record<string, string> = { ...overrides };
  for (const target of Object.values(RICH_COLOR_TOKEN_MAP)) {
    const value = overrides[target.cssVar];
    if (typeof value === 'string' && value !== '') {
      aliased[richColorAliasVar(target.cssVar)] = value;
    }
  }
  return aliased as T & Record<string, string>;
}

/**
 * Resolve a span color to a CSS value, or undefined (= do not set a color).
 *
 * - `token:<key>`: resolved from `colors` when the theme object is at hand;
 *   otherwise falls back to the funnel-root alias var and then to the theme
 *   CSS variable, so tokens still resolve through the themed render root
 *   (public player / phone preview set the vars via buildFunnelSurfaceStyle).
 *   WEB-1710: чтение идёт через двойник `--rich-*` — переменную темы
 *   перекрывает обёртка блока, и покраска куска слетала вместе с ней.
 *   Unknown key → undefined, no throw.
 * - literal: passed through only when it matches the hex/rgb allowlist
 *   (defense-in-depth on top of the BE write gate).
 */
export function resolveRichColor(
  color: string | undefined,
  colors: ThemeColors | undefined,
): string | undefined {
  if (!color) return undefined;
  if (color.startsWith(TOKEN_PREFIX)) {
    const target = RICH_COLOR_TOKEN_MAP[color.slice(TOKEN_PREFIX.length)];
    if (!target) return undefined;
    const resolved = colors?.[target.themeKey];
    if (typeof resolved === 'string' && resolved !== '') return resolved;
    // WEB-1710: сперва двойник корня воронки (его блок не перекрывает), и
    // только запасной веткой — прежняя переменная темы.
    return `var(${richColorAliasVar(target.cssVar)}, var(${target.cssVar}))`;
  }
  return isAllowedLiteralRichColor(color) ? color : undefined;
}
